<template>
	<Autocomplete
		:key="name"
		v-model="model"
		:icon="icon"
		:item-value="permission ? 'document_type_id' : 'id'"
		:item-title="
			// Runs on both shapes: the documenttype entity (acronym/display_label) and
			// the role pivot row (name/doctype_name, no acronym). Drop what is missing
			// instead of interpolating `undefined` into the title.
			(item: Partial<Api.DocumentType> & { doctype_name?: string }) =>
				[
					item.acronym,
					item.display_label ?? item.name ?? item.doctype_name ?? item.acronym,
				]
					.filter(Boolean)
					.join(' - ')
		"
		:label="label"
		:multiple="multiple"
		:name="name"
		:params="{ of: 'acronym' }"
		display-name="Dokumentenarten"
		endpoint="documenttypes"
		:data-test="`${dataTest ?? 'documenttypes'}-autocomplete`"
		:chips="permission || chips"
		v-bind="$attrs"
	>
		<template v-if="permission" #chip="{ props, item, selected, index, to }">
			<v-chip
				v-if="
					(index < limit && selected.length >= 10) ||
					showAll ||
					selected.length <= 10
				"
				v-bind="props"
				:text="item.title"
				label
				:to="to"
			>
				<span>{{ item.raw.display_label || item.raw.name }}</span>

				<v-btn
					v-tooltip:bottom="{
						text: item.raw.unlimited ? unlimited.text : startOf.text,
					}"
					:icon="item.raw.unlimited ? unlimited.icon : startOf.icon"
					class="ml-1"
					variant="flat"
					tile
					@click="item.raw.unlimited = !item.raw.unlimited"
				>
					<v-icon color="primary"> </v-icon>
				</v-btn>
			</v-chip>
			<v-btn
				v-else-if="index === limit"
				color="primary"
				size="x-small"
				@click.stop="showAll = !showAll"
				>Alle {{ selected.length }} anzeigen...
			</v-btn>
			<v-btn
				v-if="showAll && selected.length > 10 && index === selected.length - 1"
				color="primary"
				size="x-small"
				@click.stop="showAll = !showAll"
				>Ausblenden
			</v-btn>
		</template>
		<template #item="{ item, props }">
			<v-list-item
				v-bind="props"
				:title="`${item.raw.acronym} / ${item.raw.display_label || item.raw.name}`"
			>
			</v-list-item>
		</template>

		<template v-if="$slots.append" #append>
			<slot name="append"></slot>
		</template>
	</Autocomplete>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import Autocomplete from '../Validation/Autocomplete.vue'

defineProps({
	icon: {
		default: 'fa-regular fa-book',
		type: String,
	},
	label: {
		default: 'entity.documenttype.name',
		type: String,
	},
	limit: {
		default: 5,
		type: Number,
	},
	multiple: {
		default: false,
		type: Boolean,
	},
	name: {
		default: 'documenttypes',
		type: String,
	},
	optionDelete: {
		default: false,
		type: Boolean,
	},
	permission: {
		default: false,
		type: Boolean,
	},
	returnIdOnSelect: {
		default: false,
		type: Boolean,
	},
	dataTest: {
		type: String,
		default: undefined,
	},
	chips: {
		default: false,
		type: Boolean,
	},
})

const showAll = ref(false)
const startOf = ref({
	icon: 'fal fa-greater-than-equal',
	text: 'ab Beginn der PFkt',
})
const unlimited = ref({ icon: 'fal fa-infinity', text: 'unbegrenzter Zugriff' })

const model = defineModel()
</script>

<style scoped></style>
